import React, { useCallback, useEffect, useMemo, useState, } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { useNavigate,useLocation } from "react-router";
import { Grid } from "@mui/material";
import PageHeaderComponent from "../shared/page-header.component";
import ConfirmDialog from "../shared/confirm-dialog.component";
import AnimateLoader from "../shared/loaders/animate-loader.component";
import TierLoader from "../shared/loaders/tiers-loader.json";
import NewPlanCard from "./NewPlanCard";
import { AddNewTierDarkIcon, AddNewTierIcon ,NewInfoCircleIcon} from "../../icon";
import { AppRoutes } from "../routes/route.path";
import { appNote, buttonLabels, ToasterTexts } from "../shared/constants";
import { ToasterType } from "../shared/toaster/toaster.model";
import * as selector from "../../selectors/selectors";
import { PlansService } from "../../services/plans.service";
import { LoadNewPlans ,LoadPhoneNumberGroups} from "../../actions";
import { setHeaderTitle, setRefreshHandler } from "../../helpers/header.helper";
import setToast from "../../helpers/toaster.helper";
import { Plan, PlanType } from "../../models/plans.model";
import CreatEditAddon from "./CreateEditAddon";
import NewAddOnCard from "./NewAddonCard";
import { PhoneNumbergroup, } from "../../models/phone-number.model";
import { LocalStorage } from "../../services/UtilityService";
import PhoneNumberGroups from "../PhoneNumberGroup";
import { Role } from "../../models/roles.model";
import { UserService } from "../../services/UserService";




function NewPlans() {

    const [state, setState] = useState({
        show: false,
        planID: "",
        text: "",
        action: "",
        loading: false
    });

    const [selectSheet, setSelectedSheet] = useState<Array<{ id: string, count: number }>>([]);
    const [showCreatAddon, setShowCreateAddon] = useState<boolean>(false);
    const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [selectedPlan, setSelectedPlan] = useState<Plan>();
    const [searchText, setSearchText] = useState('');
    const [showCreateGroup, setShowCreateGroup] = useState<boolean>(false);

    const dispatch = useDispatch();
    const location = useLocation();
    const organizationId = UserService.getCurrentUserOrgId();
    const phoneNumberGroups: PhoneNumbergroup[] = useSelector(selector.phonenumbergroups);
    

    const { newPlans: Plan, loadingNewPlans } = useSelector((s: any) => ({
        newPlans: selector.newPlans(s),
        loadingNewPlans: selector.loadingNewPlans(s)
    }),
        shallowEqual
    );

    const NewPlans: Plan[] = useSelector(selector.newPlans)

    const gridTitle = useMemo(() => {
        if (!NewPlans?.length) return "";
        const plans = NewPlans.filter(x => x.PlanType === PlanType.TIER);
        const addons = NewPlans.filter(x => x.PlanType === PlanType.ADD_ON);
        const parts = [];
        if (plans.length) { parts.push(`${plans.length} plan${plans.length === 1 ? "" : "s"}`); }
        if (addons.length) { parts.push(`${addons.length} add-on${addons.length === 1 ? "" : "s"}`); }
        return parts.join(" and ");
    }, [NewPlans]);

    const navigate = useNavigate();

    const actions = useMemo(() => [{
        OnClick: () => navigate(AppRoutes.ADD_NEW_PLAN.replace(':PlanID', "addPlan")),
        ButtonLabel: buttonLabels.ADD_NEW_TIER,
        Icon: AddNewTierIcon,
        ClassName: "mr-2",
        Variant: "primary-btn"
    }], [navigate]);

    useEffect(() => {
        LoadNewPlans(dispatch);
        setHeaderTitle("Plans");
        setRefreshHandler(() => LoadNewPlans(dispatch));
    }, [dispatch]);

    /** Convenience: fetch a plan and some common values once */
    const getPlanById = useCallback((planId: string) => NewPlans.find((x: Plan) => x.Id === planId), [NewPlans]);

    const openDisablePlanDialog = useCallback(
        (id: string) => {
            const row = getPlanById(id);
            const isDeleted = !!row?.IsDeleted;
            setState({
                show: true,
                planID: id,
                text: isDeleted ? appNote.UNHIDE_PLAN : appNote.HIDE_PLAN,
                action: isDeleted ? "Unhide" : "Hide",
                loading: false
            });
        },
        [getPlanById]
    );

    const disablePlan = useCallback(async () => {
        setState((p) => ({ ...p, loading: true }));
        const plan = getPlanById(state.planID);

        await PlansService.disableNewPlan(state.planID);
        setState((p) => ({ ...p, loading: false, action: "", planID: "", show: false }));
        LoadNewPlans(dispatch);
        setToast(!plan?.IsDeleted ? ToasterTexts.TIER_HIDE_SUCCESS.replace("<Name>", plan?.Name || "") : ToasterTexts.TIER_UNHIDE_SUCCESS.replace("<Name>", plan?.Name || ""), ToasterType.SUCCESS);
    }, [dispatch, getPlanById, state.planID]);

    const deletePlan = useCallback(async () => {
        setState((p) => ({ ...p, loading: true }));
        const name = getPlanById(state.planID)?.Name || "";

        await PlansService.deleteNewPlan(state.planID);
        setState((p) => ({ ...p, loading: false, action: "", planID: "", show: false }));
        LoadNewPlans(dispatch);
        setToast(ToasterTexts.TIER_DELETE_SUCCESS.replace("<Name>", name), ToasterType.SUCCESS);
    }, [dispatch, getPlanById, state.planID]);

    const closeConfirmDialog = useCallback((isConfirm: boolean) => {
        if (isConfirm) {
            (state.action === "Hide" || state.action === "Unhide") ? disablePlan() : deletePlan();
        }
        setState((p) => ({ ...p, show: false, action: "" }));
    }, [disablePlan, deletePlan, state.action]);

    const searchPlans = useCallback((e: any) => {
        setSearchText(e?.target?.value)
        // TODO: debounce & update search slice if needed.
    }, []);

    const onSelectionChange = (id: string, count: number) => {
        const existing = selectSheet?.find(x => x.id === id);
        if (existing) {
            setSelectedSheet(selectSheet.map(x => x.id === id ? { ...x, count: count } : x).filter(x => x.count > 0));
        } else {
            setSelectedSheet([...selectSheet, { id, count: count }]);
        }
    }

    const openEditPlan = (plan: Plan, IsAddOn?: boolean) => {
        if (plan.PlanType === PlanType.TIER) {
            navigate(AppRoutes.ADD_NEW_PLAN.replace(':PlanID', plan.Id))
        }
        else {
            setIsEdit(true);
            setSelectedPlan(plan)
            setShowCreateAddon(true)
        }
    }

    const openDuplicatePlan = (plan: Plan, IsAddOn?: boolean) => {
        if (plan.PlanType === PlanType.TIER) {
            navigate(AppRoutes.ADD_NEW_PLAN.replace(':PlanID', plan.Id + "duplicate"))
        }
        else {
            setIsDuplicate(true);
            setSelectedPlan(plan)
            setShowCreateAddon(true)
        }
    }

    const openDeletePlanDialog = (id) => {
        setState({
            show: true,
            planID: id,
            text: appNote.DELETE_PLAN,
            action: buttonLabels.DELETE,
            loading: false
        });
    }

    const openAddNewAddOnPlan = (id: string) => {
        setShowCreateAddon(true)
        setIsDuplicate(false)
        setIsEdit(false)
    }

    const closeCreateAddon = (isConfim?: boolean) => {
        if (isConfim) {
            LoadNewPlans(dispatch);
        }
        setShowCreateAddon(false)
        setSelectedPlan(undefined)
        setIsEdit(false)
        setIsDuplicate(false)
    }

    const getfilterPlans = (type) => {
        return NewPlans.filter(x => x.PlanType === type).filter(x => searchText ? ((x.Name)?.toString()?.toLowerCase()?.includes(searchText.toLowerCase())
            || (x.FootNote)?.toString()?.toLowerCase()?.includes(searchText.toLowerCase())
            || (x.Price)?.toString()?.toLowerCase()?.includes(searchText.toLowerCase())
            || (x.YPrice)?.toString()?.toLowerCase()?.includes(searchText.toLowerCase())
            || (x.PlanType as number === 0 ? "Main Feature" : x.PlanType === PlanType.TIER ? "Additional Feature" : "Additional Purchase")?.toLowerCase()?.includes(searchText.toLowerCase())) : true)
    }


    return (
        <div className="h-p-100 w-p-100">
            <PageHeaderComponent
                HeaderText="New Plans"
                GridTitle={gridTitle}
                Count={0}
                Reload={() => { }}
                Actions={[{
                    OnClick: () => navigate(AppRoutes.ADD_NEW_PLAN.replace(':PlanID', "addPlan")),
                    ButtonLabel: buttonLabels.ADD_NEW_TIER,
                    Icon: AddNewTierIcon,
                    ClassName: "mr-2",
                    Variant: "primary-btn"
                },
                {
                    OnClick: () => openAddNewAddOnPlan(""),
                    ButtonLabel: buttonLabels.ADD_NEW_ADDON_PLAN,
                    Icon: AddNewTierDarkIcon,
                    ClassName: "mr-2",
                    Variant: "secondary-btn",
                }
                ]}
                ShowFilterIcon={false}
                IsFilterApplied={false}
                HandleFilter={() => { }}
                onSearch={searchPlans}
                PlaceHolder="Find a Plan..."
                ShowMultiActionButtons={false}
            />
            {loadingNewPlans ? (
                <Grid container spacing={2} className="my-2 mx-4">
                    {Array.from({ length: 3 }).map((_, idx) => (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={idx}
                            style={{ display: "flex", justifyContent: "center" }}
                        >
                            <AnimateLoader
                                LoaderAnimation={TierLoader}
                                className="w-p-100"
                                style={{ maxWidth: "330px" }}
                            />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Grid container className="plans-grid o-y-scroll h-p-90 d-flex">
                    {getfilterPlans(PlanType.TIER).map((plan, i) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={3} className="my-2 mx-4" key={plan.Id}>
                            <NewPlanCard
                                key={plan.Id}
                                Index={i}
                                Plan={plan}
                                ShowAddOnOptions
                                OpenDisablePlanDialog={(id) => openDisablePlanDialog(id)}
                                SelectionCount={(selectSheet.filter((x) => x.id === plan.Id)[0]?.count || 0)}
                                OnSelectionChange={(count) => { onSelectionChange(plan.Id, count); }}
                                PlanSelected={selectSheet.length > 0}
                                HideBuyNow={false}
                                EnableCounter={false}
                                OpenEditPlan={openEditPlan}
                                Duplicate={openDuplicatePlan}
                                OpenDeletePlanDialog={openDeletePlanDialog}
                            />
                        </Grid>
                    ))}
                    {getfilterPlans(PlanType.ADD_ON).map((plan, i) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={3} className="my-2 mx-4" key={plan.Id}>
                            <NewAddOnCard
                                key={i}
                                Plan={plan}
                                HideBuyNow={false}
                                ShowAddOnOptions
                                Duplicate={openDuplicatePlan}
                                openEditPlan={openEditPlan}
                                Index={(i + 1) % 2 === 0}
                                EnableCounter={false}
                                PlanSelected={false}
                                OpenDisablePlanDialog={(id) => openDisablePlanDialog(id)}
                                OpenDeletePlanDialog={openDeletePlanDialog}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {state.show && (
                <ConfirmDialog
                    ButtonName={state.action}
                    onClose={closeConfirmDialog}
                    Show={state.show}
                    Text={state.text}
                    HideCancel={false}
                    Loading={state.loading}
                />
            )}

            {
                showCreatAddon && <CreatEditAddon
                    show={showCreatAddon}
                    IsEdit={isEdit}
                    IsDuplicate={isDuplicate}
                    Tier={selectedPlan}
                    OnClose={closeCreateAddon}
                />
            }
        </div>
    );
}

export default NewPlans;